import { useCallback, useEffect, useRef, useState } from "react";
import { Download, RefreshCw, ShieldCheck, X } from "lucide-react";
import { invoke } from "@tauri-apps/api/core";

const SIX_HOURS = 6 * 60 * 60 * 1000;

function isDesktopApp() {
  return typeof window !== "undefined" && Boolean(window.__TAURI_INTERNALS__);
}

export default function DesktopUpdater() {
  const desktop = isDesktopApp();
  const [checking, setChecking] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [update, setUpdate] = useState(null);
  const [error, setError] = useState("");
  const [dismissedVersion, setDismissedVersion] = useState("");
  const mounted = useRef(true);

  const checkNow = useCallback(async ({ quiet = false } = {}) => {
    if (!desktop || checking || installing) return;
    setChecking(true);
    if (!quiet) setError("");
    try {
      const info = await invoke("check_desktop_update");
      if (!mounted.current) return;
      if (info?.available) {
        setUpdate(info);
        if (dismissedVersion && dismissedVersion !== info.latestVersion) {
          setDismissedVersion("");
        }
      } else {
        setUpdate(null);
        if (!quiet) setError("");
      }
    } catch (err) {
      if (!quiet && mounted.current) {
        setError(String(err || "Update check failed"));
      }
    } finally {
      if (mounted.current) setChecking(false);
    }
  }, [checking, desktop, dismissedVersion, installing]);

  useEffect(() => {
    mounted.current = true;
    if (!desktop) return () => { mounted.current = false; };

    const startup = window.setTimeout(() => checkNow({ quiet: true }), 3500);
    const interval = window.setInterval(() => checkNow({ quiet: true }), SIX_HOURS);
    return () => {
      mounted.current = false;
      window.clearTimeout(startup);
      window.clearInterval(interval);
    };
    // One scheduler per desktop session; the scheduled check intentionally
    // uses the launch-time callback to avoid restarting the timer on UI state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktop]);

  if (!desktop) return null;

  const install = async () => {
    if (!update?.latestVersion || installing) return;
    setInstalling(true);
    setError("");
    try {
      await invoke("install_desktop_update", { requestedVersion: update.latestVersion });
    } catch (err) {
      setInstalling(false);
      setError(String(err || "Update installation failed"));
    }
  };

  const showUpdate = update?.available && dismissedVersion !== update.latestVersion;

  return (
    <>
      <button
        type="button"
        onClick={() => checkNow({ quiet: false })}
        disabled={checking || installing}
        className="fixed bottom-4 right-4 z-[90] inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 px-3 py-2 text-[11px] font-semibold text-slate-600 shadow-lg backdrop-blur hover:border-slate-400 hover:text-slate-900 disabled:opacity-60"
        title="Check for SGP Office OS updates"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${checking ? "animate-spin" : ""}`} />
        {checking ? "Checking…" : "Updates"}
      </button>

      {showUpdate && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/55 p-6 backdrop-blur-sm">
          <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
              <div className="flex gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">Signed update available</p>
                  <h2 className="mt-1 text-xl font-bold text-slate-950">SGP Office OS v{update.latestVersion}</h2>
                  <p className="mt-1 text-xs text-slate-500">Installed: v{update.currentVersion}</p>
                </div>
              </div>
              {!installing && (
                <button
                  type="button"
                  onClick={() => setDismissedVersion(update.latestVersion)}
                  className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Remind me later"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="px-6 py-5">
              <p className="text-sm leading-6 text-slate-600">
                This update was downloaded from the official SGP Software GitHub release and will only install after SHA-256 and Windows publisher-signature verification.
              </p>
              {update.notes && (
                <div className="mt-4 max-h-32 overflow-auto rounded-xl bg-slate-50 p-3 text-xs leading-5 text-slate-600 whitespace-pre-wrap">
                  {update.notes.slice(0, 1600)}
                </div>
              )}
              {error && <p className="mt-4 rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</p>}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/70 px-6 py-4">
              {!installing && (
                <button
                  type="button"
                  onClick={() => setDismissedVersion(update.latestVersion)}
                  className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-white"
                >
                  Later
                </button>
              )}
              <button
                type="button"
                onClick={install}
                disabled={installing}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
              >
                {installing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {installing ? "Installing & restarting…" : "Update & restart"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!showUpdate && error && (
        <div className="fixed bottom-16 right-4 z-[95] max-w-sm rounded-xl border border-rose-200 bg-white px-4 py-3 text-xs text-rose-700 shadow-lg">
          {error}
        </div>
      )}
    </>
  );
}
